import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';

import Page from '../../../components/Page';
import Card from '../../../components/Card';
import { LoadingOverlay } from '../../../components/Loading';
import { updateParams, paramsEqual } from '../../../helpers/urlParams';

import { State as DraftsState } from '../reducer';
import OrdersTable from './DraftsTable';
import Filters from './Filters';
import ListMessage from './ListMessage';
import Paging from './Paging';
import { DraftOrder } from '../../../types/orders';
import { GetDraftsReason } from '../actions';
import { getGapPageArgs, getNextPageArgs, isDraftOrder } from '../helpers';
import { getCurrentLanguage } from '../../../i18n';

interface MatchParams {
    accountId: string;
}

export interface DraftListProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getDrafts: (accountId: string, params: URLSearchParams, origin: GetDraftsReason) => void;
    createDraftOrdersCsv: (aid: string, orders: DraftOrder[], t: (o: string) => string, language: string) => void;
    createDraftOrdersPdf: (aid: string, orders: DraftOrder[], t: (o: string) => string, language: string) => void;
    drafts: DraftsState;
}

interface DraftListState {
    isSearching: boolean;
    timeoutReference: number | undefined;
}

class DraftList extends Component<DraftListProps, DraftListState> {
    componentDidMount() {
        const { limit, drafts, accountId, params } = this.props.drafts;
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
        const urlAccountId = this.props.match.params.accountId;
        const hasOrders = drafts.length > 0;
        const isSameAccount = accountId === urlAccountId;
        const hasSameParams = paramsEqual(requestQueryParams, params, ['limit', 'starting_after']);
        const reason = hasOrders && isSameAccount && hasSameParams ? GetDraftsReason.refresh : GetDraftsReason.mount;
        this.props.getDrafts(urlAccountId, requestQueryParams, reason);
    }

    componentDidUpdate(prevProps: DraftListProps, prevState: any) {
        if (this.props.location.search !== prevProps.location.search) {
            const { limit } = this.props.drafts;
            const urlAccountId = this.props.match.params.accountId;
            const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
            const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
            this.props.getDrafts(urlAccountId, requestQueryParams, GetDraftsReason.update);
        }
    }

    render() {
        const { t, i18n, getDrafts: getOrders, createDraftOrdersCsv, createDraftOrdersPdf } = this.props;
        const { drafts, isLoading, hasNext, isLoadingNext } = this.props.drafts;
        const limit = this.props.drafts.limit.toString();
        const accountId = this.props.match.params.accountId;
        const firstOrder = drafts.find((x) => x.id !== 'gap') as DraftOrder | undefined;

        const currency = firstOrder ? firstOrder.order.currency : 'NOK';

        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const refreshParams = updateParams(locationQueryParams, { limit });
        const refresh = () => {
            getOrders(accountId, refreshParams, GetDraftsReason.refresh);
        };

        const nextPageArgs = getNextPageArgs(drafts);
        const nextParams = updateParams(locationQueryParams, { ...nextPageArgs, limit });
        const next = () => {
            getOrders(accountId, nextParams, GetDraftsReason.next);
        };

        const fillGap = (id: string) => {
            const gapPageArgs = getGapPageArgs(drafts, id);
            const gapParams = updateParams(locationQueryParams, { ...gapPageArgs, limit });
            getOrders(accountId, gapParams, GetDraftsReason.refresh);
        };

        const createCsv = () => {
            createDraftOrdersCsv(accountId, drafts.filter(isDraftOrder) as DraftOrder[], t, i18n.language);
        };

        const createPdf = () => {
            createDraftOrdersPdf(accountId, drafts.filter(isDraftOrder) as DraftOrder[], t, getCurrentLanguage(i18n));
        };

        return (
            <Page title={t('orders.drafts.title')} hasEnvFrame={true}>
                <Card noPadding>
                    <Wrapper>
                        <Filters
                            history={this.props.history}
                            location={this.props.location}
                            refresh={refresh}
                            createCsv={createCsv}
                            createPdf={createPdf}
                        />
                        <OrdersTable drafts={drafts} accountId={accountId} fillGap={fillGap} />
                        <RelativeGroup>
                            <Paging next={next} isLoading={isLoading} hasNext={hasNext} />
                            <ListMessage
                                drafts={drafts}
                                isLoading={isLoading}
                                hasNext={hasNext}
                                isLoadingNext={isLoadingNext}
                                accountId={accountId}
                                queryParams={locationQueryParams}
                                currency={currency}
                            />
                            {isLoading && <LoadingOverlay />}
                        </RelativeGroup>
                    </Wrapper>
                </Card>
            </Page>
        );
    }
}

export default withTranslation()(DraftList);

const Wrapper = styled.div`
    position: relative;
    box-sizing: border-box;
`;

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
`;
